import React, { useState, useEffect, ChangeEvent, FormEvent, useCallback } from "react";
import {
  getPhoneValidationMessage as validatePhoneNumber,
  PHONE_INPUT_MAX_DIGITS,
} from "../utils/phoneValidation";
import {
  Drawer,
  Box,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import themestyle from "../theme";
import {
  MSG,
  INPUT_REGEX,
  FORM_LABELS,
  FORM_PLACEHOLDERS,
  MESSAGES,
} from "../utils/const";
import { countries, getEmojiFlag, TCountryCode } from "countries-list";
import * as Flags from "country-flag-icons/react/3x2";
import {
  Autocomplete,
} from "@mui/material";

import {
  validateAdminField,
} from "../utils/validation";
import { useMutation } from "@apollo/client/react";
import { UPDATE_ADMIN } from "../graphql/mutation";
import AdminTextField from "./ViewAdminTextField";
import { AdminEditableData } from "../interfaces/auth.interface";

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onError?: (message: string) => void;
  data?: {
    id: string;
    fullName?: string;
    fullname?: string;
    email: string;
    phone?: string;
    phoneNumber?: string;
    address?: string;
    country?: string;
    postcode?: string;
    role?: string;
  };
};


const ViewAdminDrawer: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  onError,
  data,
}) => {
  const DEFAULT_COUNTRY = "India";
  const countryOptions = Object.entries(countries)
    .map(([code, country]) => ({
      code: code as TCountryCode,
      name: country.name,
      emoji: getEmojiFlag(code as TCountryCode),
      dialCode: String(country.phone?.[0] || ""),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const getCountryDialCode = (countryName: string) => {
    const matchedCountry = countryOptions.find((country) => country.name === countryName);
    return matchedCountry?.dialCode || "";
  };

  // Phone is required here; the shared libphonenumber-js util does the accurate
  // per-country length/format check once the field is non-empty.
  const getPhoneValidationMessage = (phone: string, countryName: string) => {
    if (!phone.replace(/\D/g, "")) return MSG.PHONE_REQUIRED;
    return validatePhoneNumber(phone, countryName);
  };

  const [formData, setFormData] = useState<AdminEditableData>({
    name: "",
    email: "",
    phone: "",
    address: "",
    country: "",
    postcode: "",
    role: "",
  });
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_COUNTRY);
  const [phoneCountryMenuAnchor, setPhoneCountryMenuAnchor] = useState<null | HTMLElement>(null);
  const [phoneCountrySearch, setPhoneCountrySearch] = useState("");

  React.useEffect(() => {
    if (data && open) {
      const mapped = {
        name: data.fullName || data.fullname || "",
        email: data.email || "",
        phone: data.phone || data.phoneNumber || "",
        address: data.address || "",
        country: data.country || "",
        postcode: data.postcode || "",
        role: data.role || "",
      };
      setFormData(mapped);
      setPhoneCountry(data.country || DEFAULT_COUNTRY);
      setOriginalData(mapped);
      setIsEditMode(false);
      setErrors({});
    }
    if (!open) {
      setErrors({});
      setIsEditMode(false);
      setPhoneCountryMenuAnchor(null);
      setPhoneCountrySearch("");
    }
  }, [data, open]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [updateAdmin, { loading: updating }] = useMutation(UPDATE_ADMIN);
  const [originalData, setOriginalData] =
    useState<AdminEditableData | null>(null);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "name" || name === "country") {
      setFormData((prev) => ({
        ...prev,
        [name]: value.replace(INPUT_REGEX.ONLY_ALPHABETS_SPACE, ""),
      }));
      return;
    }
    if (name === "phone") {
      const digitsOnly = value.replace(/\D/g, "");
      setFormData((prev) => ({ ...prev, phone: digitsOnly }));
      setErrors((prev) => ({
        ...prev,
        phone: digitsOnly ? getPhoneValidationMessage(digitsOnly, phoneCountry) : "",
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const msg = name === "phone" ? getPhoneValidationMessage(value, phoneCountry) : validateAdminField(name, value);
    setErrors((prev) => ({ ...prev, [name]: msg }));
  };

  const handleCountryChange = (event: any, newValue: { code: string; name: string } | null) => {
    const value = newValue ? newValue.name : "";
    setFormData((prev) => ({
      ...prev,
      country: value,
    }));
    const msg = validateAdminField("country", value);
    setErrors((prev) => ({ ...prev, country: msg }));
  };

  const handlePhoneCountryMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!isEditMode) return;
    setPhoneCountryMenuAnchor(event.currentTarget);
  };

  const handlePhoneCountryMenuClose = () => {
    setPhoneCountryMenuAnchor(null);
    setPhoneCountrySearch("");
  };

  const handlePhoneCountryChange = (newValue: { code: string; name: string } | null) => {
    const nextCountry = newValue?.name || DEFAULT_COUNTRY;
    setPhoneCountry(nextCountry);
    setErrors((prev) => ({
      ...prev,
      phone: formData.phone ? getPhoneValidationMessage(formData.phone, nextCountry) : "",
    }));
  };
  const handleCancel = () => {
   
    onClose();
  };
  const handleSave = async () => {
    try {
      const result: any = await updateAdmin({
        variables: {
          userId: data?.id,
          input: {
            fullname: formData.name,
            email: formData.email,
            phoneNumber: formData.phone,
            address: formData.address,
            country: formData.country,
            postcode: formData.postcode,
          },
        },
      });

      const response = result.data;

      if (response?.updateAdmin?.success) {
        setOriginalData({ ...formData });
        setIsEditMode(false);
        onSuccess?.();
        onClose();
      } else {
        onError?.(
          response?.updateAdmin?.message || "Failed to update admin"
        );
      }
    } catch (err: any) {
      onError?.(err.message || "Network error");
    }
  };



  const textFieldSX = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      minHeight: { xs: "38px", sm: "44px" },
      "& input": {
        fontSize: { xs: "11px", sm: "14px" },
        padding: { xs: "12px", sm: "10px 12px" },
        fontFamily: themestyle.fontFamily.Roboto,
      },
      "& textarea": {
        fontSize: { xs: "12px", sm: "14px" },
        padding: "12px",
        fontFamily: themestyle.fontFamily.Roboto,
      },
      "&.Mui-focused fieldset": {
        borderColor: themestyle.colors.activeGreen,
        borderWidth: "1px",
      },
    },
    "& .MuiFormHelperText-root": {
      fontSize: { xs: "9px", sm: "12px" },
      marginLeft: "4px",
      fontFamily: themestyle.fontFamily.Roboto,
    },
  };

  const Label = ({ text }: { text: string }) => (
    <Box
      sx={{
        fontSize: { xs: "12px", sm: "15px" },
        fontWeight: 500,
        mb: 0.6,
        fontFamily: themestyle.fontFamily.Roboto,
      }}
    >
      {text}
    </Box>
  );
  const handleClose = () => {
    onClose();
  };

  const filteredPhoneCountryOptions = countryOptions.filter((country) => {
    const query = phoneCountrySearch.trim().toLowerCase();
    if (!query) return true;

    return (
      country.name.toLowerCase().includes(query) ||
      country.dialCode.includes(query) ||
      country.code.toLowerCase().includes(query)
    );
  });

  const selectedPhoneCountry =
    countryOptions.find((country) => country.name === phoneCountry) ||
    countryOptions.find((country) => country.name === DEFAULT_COUNTRY) ||
    null;

  const selectedDialCode = getCountryDialCode(phoneCountry);
  const isFormValid =
    formData.name.length >= 2 &&
    !!formData.email &&
    !validateAdminField("email", formData.email) &&
    !getPhoneValidationMessage(formData.phone, phoneCountry) &&
    !validateAdminField("postcode", formData.postcode) &&
    !validateAdminField("country", formData.country) &&
    !validateAdminField("address", formData.address);

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box
        sx={{
          width: { xs: "70vw", sm: 400 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ px: 2, pt: { xs: 1, sm: 2 }, }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: { xs: 1, sm: 2 },
              pb: { xs: 0.8, sm: 1.5 },
              borderBottom: `1px solid ${themestyle.colors.border}`,
            }}
          >
            <Box
              sx={{
                fontWeight: 600,
                fontSize: { xs: "16px", sm: "20px" },
                fontFamily: themestyle.fontFamily.Roboto,
              }}
            >
              {isEditMode ? "Edit admin" : "View admin"}
            </Box>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto", px: 2, scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none", }, }}>
          <form>
            <AdminTextField
              name="name"
              label="Full Name"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              isEditMode={isEditMode}
              error={errors.name}
            />
            <AdminTextField
              name="email"
              label="Email"
              value={formData.email}
              onChange={handleChange}
              disabled={true}
              isEditMode={false}
              onBlur={handleBlur}
            />
            <Box mt={2}>
              <Label text="Phone Number" />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: { xs: "38px", sm: "44px" },
                  border: "1px solid",
                  borderColor: errors.phone ? themestyle.colors.error : "rgba(0, 0, 0, 0.23)",
                  borderRadius: "8px",
                  backgroundColor: isEditMode ? themestyle.colors.white : "#F5F5F5",
                  overflow: "hidden",
                  "&:focus-within": {
                    borderColor: isEditMode ? themestyle.colors.activeGreen : "rgba(0, 0, 0, 0.23)",
                  },
                }}
              >
                <Box
                  onClick={handlePhoneCountryMenuOpen}
                  sx={{
                    width: "fit-content",
                    maxWidth: 120,
                    px: 1.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    borderRight: "1px solid #E4E7EC",
                    cursor: isEditMode ? "pointer" : "default",
                    flexShrink: 0,
                    height: "100%",
                    fontFamily: themestyle.fontFamily.Roboto,
                    opacity: isEditMode ? 1 : 0.75,
                  }}
                >
                  {(() => {
                    const Flag = selectedPhoneCountry ? (Flags as any)[selectedPhoneCountry.code] : null;

                    return (
                      <>
                        <Box sx={{ width: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {Flag ? (
                            <Flag style={{ width: 20, height: 14, borderRadius: "2px" }} />
                          ) : (
                            <Typography sx={{ fontSize: 13, fontFamily: themestyle.fontFamily.Roboto }}>
                              {selectedPhoneCountry?.emoji || "🌐"}
                            </Typography>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1D2939", fontFamily: themestyle.fontFamily.Roboto }}>
                          {selectedDialCode ? `+${selectedDialCode}` : "Code"}
                        </Typography>
                        <KeyboardArrowDownRoundedIcon sx={{ fontSize: 17, color: "#98A2B3", opacity: isEditMode ? 1 : 0 }} />
                      </>
                    );
                  })()}
                </Box>

                <TextField
                  name="phone"
                  value={formData.phone}
                  placeholder={FORM_PLACEHOLDERS.PHONE_UK}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={false}
                  fullWidth
                  variant="standard"
                  disabled={!isEditMode}
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: PHONE_INPUT_MAX_DIGITS,
                  }}
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    flex: "1 1 0",
                    minWidth: 0,
                    height: "100%",
                    "& .MuiInputBase-root": {
                      display: "flex",
                      alignItems: "center",
                      px: 1.5,
                      width: "100%",
                      height: "100%",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: { xs: "11px", sm: "14px" },
                      color: "#1D2939",
                      padding: 0,
                      height: "100%",
                      boxSizing: "border-box",
                      fontFamily: themestyle.fontFamily.Roboto,
                      WebkitTextFillColor: isEditMode ? "#1D2939" : "#1D2939",
                    },
                  }}
                />
              </Box>
              {errors.phone ? (
                <Box
                  sx={{
                    fontSize: { xs: "9px", sm: "12px" },
                    color: themestyle.colors.error,
                    marginLeft: "4px",
                    marginTop: "4px",
                    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                  }}
                >
                  {errors.phone}
                </Box>
              ) : null}
              <Menu
                anchorEl={phoneCountryMenuAnchor}
                open={Boolean(phoneCountryMenuAnchor)}
                onClose={handlePhoneCountryMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    width: 320,
                    maxHeight: 380,
                    borderRadius: "16px",
                    border: "1px solid #D0D5DD",
                    boxShadow: "0 20px 40px rgba(16, 24, 40, 0.12)",
                    p: 0.5,
                  },
                }}
                MenuListProps={{ disablePadding: true }}
              >
                <Box sx={{ p: 1 }}>
                  <TextField
                    value={phoneCountrySearch}
                    onChange={(event) => setPhoneCountrySearch(event.target.value)}
                    placeholder="Search"
                    fullWidth
                    size="small"
                    autoFocus
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    InputProps={{
                      startAdornment: <SearchOutlinedIcon sx={{ color: "#98A2B3", mr: 1 }} />,
                    }}
                  />
                </Box>
                <Box sx={{ maxHeight: 300, overflowY: "auto", py: 0.5 }}>
                  {filteredPhoneCountryOptions.map((country) => {
                    const Flag = (Flags as any)[country.code];
                    const isActive = country.name === phoneCountry;

                    return (
                      <MenuItem
                        key={country.code}
                        onClick={() => {
                          handlePhoneCountryChange(country);
                          handlePhoneCountryMenuClose();
                        }}
                        sx={{
                          mx: 0.5,
                          mb: 0.25,
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          backgroundColor: isActive ? "rgba(30, 127, 61, 0.08)" : "transparent",
                        }}
                      >
                        <Box sx={{ width: 22, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {Flag ? (
                            <Flag style={{ width: 20, height: 14, borderRadius: "2px" }} />
                          ) : (
                            <span>{country.emoji}</span>
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 14, color: "#1D2939", fontFamily: themestyle.fontFamily.Roboto }}>
                            {country.name}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#667085", fontFamily: themestyle.fontFamily.Roboto }}>
                          +{country.dialCode || "—"}
                        </Typography>
                      </MenuItem>
                    );
                  })}
                </Box>
              </Menu>
            </Box>
            <AdminTextField
              name="role"
              label="Role"
              value={formData.role}
              onChange={handleChange}
              isEditMode={isEditMode}
              disabled={true}
            />
            <AdminTextField
              name="address"
              label="Address"
              value={formData.address}
              onChange={handleChange}
              onBlur={handleBlur}
              isEditMode={isEditMode}
              error={errors.address}
              multiline={true}
              rows={2}
            />
            {isEditMode ? (
              <Box mt={2}>
                <Label text={FORM_LABELS.COUNTRY} />
                <Autocomplete
                  options={countryOptions}
                  getOptionLabel={(option) => option.name}
                  value={countryOptions.find((c) => c.name === formData.country) || null}
                  onChange={handleCountryChange}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props as any;
                    const Flag = (Flags as any)[option.code];
                    return (
                      <Box 
                        key={key}
                        component="li" 
                        {...optionProps} 
                        sx={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: 1.5, 
                          fontSize: { xs: "12px", sm: "14px" },
                          py: 1
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", width: 24, flexShrink: 0 }}>
                          {Flag ? <Flag style={{ width: "100%", height: "auto", borderRadius: "2px" }} /> : <span>{option.emoji}</span>}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          {option.name}
                        </Box>
                      </Box>
                    );
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      padding: "0px !important",
                      paddingLeft: "12px !important",
                      "& .MuiAutocomplete-input": {
                        padding: { xs: "12px !important", sm: "10px 12px !important" },
                        paddingLeft: "8px !important",
                      },
                    },
                  }}
                  renderInput={(params) => {
                    const selectedCountry = countryOptions.find((c) => c.name === formData.country);
                    const Flag = selectedCountry ? (Flags as any)[selectedCountry.code] : null;
                    
                    return (
                      <TextField
                        {...params}
                        placeholder={FORM_LABELS.COUNTRY}
                        error={!!errors.country}
                        helperText={errors.country}
                        sx={textFieldSX}
                        autoComplete="new-password"
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: 'new-password',
                        }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: selectedCountry && Flag ? (
                            <Box sx={{ display: "flex", alignItems: "center", width: 20, mr: -1, ml: 0.5 }}>
                              <Flag style={{ width: "100%", height: "auto", borderRadius: "1px" }} />
                            </Box>
                          ) : params.InputProps.startAdornment
                        }}
                      />
                    );
                  }}
                  slotProps={{
                    popper: {
                      placement: "bottom-start",
                      modifiers: [
                        {
                          name: 'flip',
                          enabled: false,
                        },
                      ],
                    },
                    paper: {
                      sx: {
                        mt: 1,
                        borderRadius: "10px",
                        boxShadow: "0px 10px 30px rgba(0,0,0,0.08)",
                        border: "1px solid #F0F0F0",
                        "& .MuiAutocomplete-listbox": {
                          maxHeight: "300px",
                          padding: "6px 0",
                          "& .MuiAutocomplete-option": {
                            padding: "10px 18px",
                            "&[aria-selected='true']": {
                              backgroundColor: "rgba(30, 127, 61, 0.08)",
                            },
                            "&.Mui-focused": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }
                        }
                      }
                    }
                  }}
                />
              </Box>
            ) : (
              <AdminTextField
                name="country"
                label="Country"
                value={formData.country}
                onChange={handleChange}
                onBlur={handleBlur}
                isEditMode={isEditMode}
                error={errors.country}
              />
            )}
            <AdminTextField
              name="postcode"
              label="Postcode"
              value={formData.postcode}
              onChange={handleChange}
              onBlur={handleBlur}
              isEditMode={isEditMode}
              error={errors.postcode}
            />
          </form>
        </Box>
        <Box
          sx={{
            mt: 3,
            px: 2,
            py: { xs: 1.5, sm: 2 },
            borderTop: `1px solid ${themestyle.colors.border}`,
            display: "flex",
            justifyContent: "flex-end",
            gap: { xs: 1, sm: 1.5 },
            backgroundColor: themestyle.colors.white,
          }}
        >

          <Button
            onClick={handleCancel}
            sx={{
              minWidth: { xs: "70px", sm: "80px" },
              height: { xs: "28px", sm: "35px" },
              borderRadius: "4px",
              backgroundColor: themestyle.colors.white,
              color: themestyle.colors.black,
              fontSize: { xs: "12px", sm: "14px" },
              fontWeight: 500,
              textTransform: "none",
              border: `1px solid ${themestyle.colors.border}`,
              "&:hover": { backgroundColor: "#F5F5F5" },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={() => {
              if (!isEditMode) {
                setIsEditMode(true);
              } else {
                handleSave();
              }
            }} disabled={isEditMode && !isFormValid}
            sx={{
              minWidth: { xs: "80px", sm: "90px" },
              height: { xs: "28px", sm: "35px" },
              borderRadius: "4px",
              backgroundColor: themestyle.colors.activeGreen,
              color: themestyle.colors.white,
              fontSize: { xs: "12px", sm: "14px" },
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                backgroundColor: themestyle.colors.secondary,
              },
              "&:disabled": {
                backgroundColor: themestyle.colors.GRAY_DISABLED,
                color: themestyle.colors.white,
              },
            }}
          >
            {isEditMode ? "Update" : "Edit"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
export default ViewAdminDrawer;
